import React from 'react';
import { css } from '@emotion/react';
import { greys, onwardNavy } from '@/styles/colors';

// Shared visual atoms for the redesigned DetailPanel tabs (Details / Items-Docs /
// Notes / Survey). Kept local to the AccessorialAuditModal so restyling here never
// leaks into the shared Accessorials/OrderInfoSection components used elsewhere.

export const FONT = 'Montserrat, Roboto, Arial, sans-serif';

// Soft accent backgrounds (not in the shared token file — scoped to this rail).
export const SOFT = {
    green: '#eaf6ec',
    gold: '#fbf1e0',
    blue: '#e9f2ff',
};

export const ACCENT = {
    green: '#59b863',
    gold: '#E28700',
    blue: '#007BFF',
};

// Compact key/value row used across the overview header + tabs.
export function KV({ label, value, accent }) {
    return (
        <div
            css={css`
                display: flex;
                justify-content: space-between;
                gap: 14px;
                padding: 6px 0;
                border-top: 1px solid #f2f2f2;
            `}
        >
            <span
                css={css`
                    font-family: ${FONT};
                    font-weight: 500;
                    font-size: 12px;
                    color: ${greys[1]};
                    flex: 0 0 auto;
                    white-space: nowrap;
                `}
            >
                {label}
            </span>
            <span
                css={css`
                    font-family: ${FONT};
                    font-weight: 700;
                    font-size: 12px;
                    color: ${accent || greys[3]};
                    text-align: right;
                    word-break: break-word;
                `}
            >
                {value}
            </span>
        </div>
    );
}

export function SectionTitle({ children, first, count }) {
    return (
        <div
            css={css`
                display: flex;
                align-items: baseline;
                gap: 6px;
                font-family: ${FONT};
                font-weight: 800;
                font-size: 11px;
                color: ${onwardNavy};
                text-transform: uppercase;
                letter-spacing: 0.06em;
                margin: ${first ? '2px 0 2px' : '20px 0 2px'};
            `}
        >
            <span>{children}</span>
            {count != null ? (
                <span
                    css={css`
                        font-weight: 700;
                        color: ${greys[0]};
                    `}
                >
                    {count}
                </span>
            ) : null}
        </div>
    );
}

export function Empty({ children, top }) {
    return (
        <div
            css={css`
                font-family: ${FONT};
                font-weight: 500;
                font-size: 12.5px;
                color: ${greys[1]};
                font-style: italic;
                padding: ${top ? '8px 0' : '10px 0'};
            `}
        >
            {children}
        </div>
    );
}

// Small rounded pill for tags/labels (SKU, barcode, note role, etc.).
export function Pill({ children, color = greys[2], background = '#f2f2f2', uppercase }) {
    return (
        <span
            css={css`
                display: inline-flex;
                align-items: center;
                font-family: ${FONT};
                font-weight: 700;
                font-size: 9.5px;
                line-height: 1;
                color: ${color};
                background: ${background};
                padding: 4px 8px;
                border-radius: 20px;
                white-space: nowrap;
                ${uppercase ? 'text-transform: uppercase; letter-spacing: 0.04em;' : ''}
            `}
        >
            {children}
        </span>
    );
}

// White bordered card used to group items / notes / survey blocks.
export function Card({ children, css: extra }) {
    return (
        <div
            css={css`
                border: 1px solid ${greys[4]};
                border-radius: 8px;
                background: #fff;
                padding: 12px 13px;
                ${extra || ''}
            `}
        >
            {children}
        </div>
    );
}

// Flex-wrapped thumbnail grid for photo/document arrays. Empty -> italic line.
export function PhotoGrid({ urls, keyPrefix, emptyLabel = 'No uploads' }) {
    if (!urls || urls.length === 0) {
        return <Empty>{emptyLabel}</Empty>;
    }
    return (
        <div
            css={css`
                display: flex;
                flex-wrap: wrap;
                gap: 8px;
                padding: 4px 0 2px;
            `}
        >
            {urls.map((url, idx) => (
                <a
                    key={`${keyPrefix}-${idx}`}
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    css={css`
                        display: inline-flex;
                        border: 1px solid ${greys[4]};
                        border-radius: 6px;
                        overflow: hidden;
                        line-height: 0;
                    `}
                >
                    <img
                        src={url}
                        alt=""
                        css={css`
                            width: 76px;
                            height: 76px;
                            object-fit: cover;
                        `}
                    />
                </a>
            ))}
        </div>
    );
}
